import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';
import { CardAvatarStack } from '@/components/ui/card-avatar-stack';
import { TypewriterText } from '@/components/custom/typewriter-text';
import { ProjectsProvider } from '../provider';
import { ProjectSchedulePlayful } from './ProjectSchedule';
import booksIcon from '@/assets/images/icons/books-3d.png';
import tiaInstrucoes from '@/assets/images/tia/TIA-INSTRUCOES.png';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

export default function ProjectsAboutPlayful({ uri }) {
  const { t } = useTranslation();
  const client = useClient();
  const { projectId } = useParams();
  const [project, setProject] = useState([]);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [mentors, setMentors] = useState([]);

  const getProject = async () => {
    setLoading(true);
    const { data, error } = await client.fetch(`projects/${projectId}`);

    setLoading(false);
    if (error) {
      toast.error('Erro ao buscar projeto.');
    } else {
      setProject(data);

      if (data?.users && data?.users.length > 0) {
        setStudents(data.users.filter(u => u.profiles.includes('Aluno') && !u.profiles.includes('Professor') && !u.profiles.includes('Administrador')));
        setMentors(data.users.filter(u => u.profiles.includes('Professor') || u.profiles.includes('Administrador')));
      }
    }
  };

  const createProjectUser = async () => {
    await client.mutate('project_users', { 'project-id': projectId });
  };

  useEffect(() => {
    getProject();
  }, []);

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: '/projetos',
        name: 'Projetos',
        isActive: false
      },
      {
        route: uri,
        name: 'Detalhamento',
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {loading ? (
        <Loader />
      ) : (
        <div className="mt-6 grid items-stretch gap-6 lg:grid-cols-[340px_1fr_280px]">
          {/* Coluna da T.I.A: mascote + balão com o resumo do projeto. */}
          <aside
            className="flex flex-col items-center rounded-[24px] p-6 shadow-card"
            style={{ background: 'var(--color-bg-lilac)' }}
          >
            <img
              src={tiaInstrucoes}
              alt="T.I.A"
              className="pointer-events-none h-[220px] w-auto object-contain"
              style={{ animation: `trilha-in 380ms ${EASE_OUT} both` }}
            />

            <div
              className="mt-4 w-full rounded-[16px] rounded-tl-none bg-white p-4 shadow-card"
              style={{ animation: `trilha-in 380ms ${EASE_OUT} 150ms both` }}
            >
              <span
                className="block text-xs font-extrabold"
                style={{ color: 'var(--color-secondary-dark)' }}
              >
                T.I.A
              </span>
              <p
                className="mb-0 text-sm font-bold leading-relaxed"
                style={{ color: '#222731' }}
              >
                <TypewriterText text={project?.about || 'Todo projeto começa com uma boa ideia! Dá uma olhada nos detalhes por aqui. 📋'} />
              </p>
            </div>

            <div
              className="mt-3 w-full rounded-[16px] rounded-tl-none bg-white p-4 shadow-card"
              style={{ animation: `trilha-in 380ms ${EASE_OUT} 700ms both` }}
            >
              <p
                className="mb-0 text-sm font-bold leading-relaxed"
                style={{ color: '#222731' }}
              >
                Trabalhar em equipe é uma ótima forma de aprender! Combine com seu grupo, capriche em cada versão enviada e conte comigo no que precisar. 🤝✨
              </p>
            </div>
          </aside>

          {/* Coluna do projeto: ilustração, título, sobre, participantes e CTA. */}
          <section
            className="relative flex flex-col overflow-hidden rounded-[24px] border border-neutral-100 bg-[var(--color-bg-cream)] p-8 shadow-card"
            style={{ animation: `trilha-in 380ms ${EASE_OUT} 150ms both` }}
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-20 rounded-t-[24px]"
              style={{ background: 'linear-gradient(180deg, rgba(214,253,255,0.5) 0%, transparent 100%)' }}
            />

            <div className="relative flex flex-wrap items-center gap-5">
              <img
                src={booksIcon}
                alt=""
                aria-hidden="true"
                className="h-24 w-24 shrink-0 object-contain drop-shadow-sm"
              />
              <div className="min-w-[200px] flex-1">
                <h1
                  className="mb-2 text-2xl font-extrabold leading-snug"
                  style={{ color: '#222731' }}
                >
                  {project?.name}
                </h1>
                <span className="text-sm font-semibold text-neutral-500">
                  {t('projects.updatedIn')} {moment(project['updated-at']).format(`MMMM [${t('projects.in')}] YYYY`)} · {t('projects.by')} {project?.user?.name}
                </span>
              </div>
            </div>

            <div className="relative mt-6 flex flex-col gap-3 rounded-[16px] bg-white p-4 shadow-card">
              <span className="text-xs font-bold uppercase tracking-wide text-neutral-400">{t('button.aboutProject')}</span>
              <p
                className="mb-0 text-sm font-semibold leading-relaxed"
                style={{ color: '#222731' }}
              >
                {project?.about}
              </p>
            </div>

            <div className="relative mt-3 flex flex-wrap gap-4 rounded-[16px] bg-white p-4 shadow-card">
              <div className="flex-1">
                <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-neutral-400">{t('projects.titleTeachers')}</span>
                {mentors?.length > 0 ? (
                  <CardAvatarStack
                    data={mentors}
                    modalTitle={t('projects.titleProjectTeacher')}
                    size="md"
                    disableUserLink
                  />
                ) : (
                  <span className="text-sm text-neutral-400">—</span>
                )}
              </div>
              <div className="flex-1">
                <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-neutral-400">{t('projects.titleStudents')}</span>
                {students?.length > 0 ? (
                  <CardAvatarStack
                    data={students}
                    modalTitle={t('projects.titleProjectStudents')}
                    size="md"
                    disableUserLink
                  />
                ) : (
                  <span className="text-sm text-neutral-400">—</span>
                )}
              </div>
            </div>

            <div className="relative mt-auto pt-8">
              <Link
                to={`/projetos/${projectId}`}
                onClick={() => createProjectUser()}
                className="group/btn relative block w-full overflow-hidden rounded-full py-4 text-center text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
                style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 4px 0 var(--color-mission-dark), 0 6px 12px rgba(245,123,46,0.35)' }}
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, transparent 100%)' }}
                />
                <span className="relative inline-flex items-center justify-center gap-2">
                  {t('button.accessProject')}
                  <FiArrowRight className="h-4 w-4 transition-transform duration-150 group-hover/btn:translate-x-0.5" />
                </span>
              </Link>
            </div>
          </section>

          {/* Coluna do cronograma: mesmo fundo do card de conquista dos simulados. */}
          <div className="flex flex-col gap-4">
            <ProjectsProvider>
              <ProjectSchedulePlayful background="var(--color-bg-amber-soft)" />
            </ProjectsProvider>
          </div>
        </div>
      )}
    </DashboardContent>
  );
}
